import { useId, useState } from 'react';
import { T } from 'gt-react';
import type { SopFeedbackReviewRequired } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** Remount for each content fingerprint so an acknowledgment cannot survive changed content. */
export function SopFeedbackPublishDialog({
  review,
  pending,
  onCancel,
  onConfirm,
}: {
  review: SopFeedbackReviewRequired;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (fingerprint: string) => void;
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const id = useId();
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <T context="review SOP feedback changes before publishing">
              Review Procedure Changes
            </T>
          </DialogTitle>
          <DialogDescription>
            <T context="publication warning for SOP feedback edits">
              These edits change instructions or structure. Existing recorded
              actions are preserved; new steps may need an agent to perform
              them.
            </T>
          </DialogDescription>
        </DialogHeader>
        <ul className="flex list-disc flex-col gap-2 pl-4 text-sm">
          {review.warnings.map((warning) => (
            <li key={`${warning.code}-${warning.stepIds.join(',')}`}>
              {warning.message}
            </li>
          ))}
        </ul>
        <div className="flex items-start gap-2">
          <Checkbox
            id={id}
            checked={acknowledged}
            onCheckedChange={(checked) => setAcknowledged(checked === true)}
          />
          <Label htmlFor={id}>
            <T context="human acknowledgment of unchanged SOP automation before publish">
              I reviewed the changes and their effect on recorded automation.
            </T>
          </Label>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={pending} onClick={onCancel}>
            <T context="cancel SOP feedback publication">Cancel</T>
          </Button>
          <Button
            disabled={pending || !acknowledged}
            onClick={() => onConfirm(review.fingerprint)}
          >
            <T context="publish reviewed SOP feedback changes">
              Publish Reviewed Changes
            </T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
